import type { SupabaseClient } from '../network/SupabaseClient';
import { axialKey } from '../math/HexMath';

export interface FogReveal {
  readonly centerQ: number;
  readonly centerR: number;
  readonly radius: number;
}

/**
 * Tracks which hexes are revealed for the current player.
 * Reveals are circles defined by axial center + radius.
 * The full revealed-set is pre-computed for O(1) lookups.
 */
export class FogState {
  private _reveals: FogReveal[] = [];
  private _revealedKeys = new Set<number>();
  private _supabase: SupabaseClient | null;
  private _worldId: string;
  private _playerId: string | null = null;
  private _loaded = false;

  constructor(supabase: SupabaseClient | null, worldId: string) {
    this._supabase = supabase;
    this._worldId = worldId;
  }

  get loaded(): boolean { return this._loaded; }
  get revealCount(): number { return this._reveals.length; }

  setPlayerId(id: string): void { this._playerId = id; }

  async loadReveals(): Promise<void> {
    if (!this._supabase || !this._playerId) {
      this._loaded = true;
      return;
    }

    const { data, error } = await this._supabase
      .from('fog_reveals')
      .select('center_q, center_r, radius')
      .eq('world_id', this._worldId)
      .eq('player_id', this._playerId);

    if (!error && data) {
      this._reveals.length = 0;
      for (let i = 0; i < data.length; i++) {
        this._reveals.push({
          centerQ: data[i].center_q,
          centerR: data[i].center_r,
          radius: data[i].radius,
        });
      }
    }

    this._rebuildSet();
    this._loaded = true;
  }

  addReveal(centerQ: number, centerR: number, radius: number): void {
    this._reveals.push({ centerQ, centerR, radius });
    this._expandCircle(centerQ, centerR, radius);
    this._persistReveal(centerQ, centerR, radius);
  }

  isRevealed(q: number, r: number): boolean {
    return this._revealedKeys.has(axialKey(q, r));
  }

  clear(): void {
    this._reveals.length = 0;
    this._revealedKeys.clear();
  }

  private _rebuildSet(): void {
    this._revealedKeys.clear();
    for (let i = 0; i < this._reveals.length; i++) {
      const rev = this._reveals[i];
      this._expandCircle(rev.centerQ, rev.centerR, rev.radius);
    }
  }

  private _persistReveal(cq: number, cr: number, radius: number): void {
    if (!this._supabase || !this._playerId) return;
    this._supabase.functions
      .invoke('reveal-fog', {
        body: {
          world_id: this._worldId,
          player_id: this._playerId,
          center_q: cq,
          center_r: cr,
          radius,
        },
      })
      .then(({ error }) => {
        if (error) console.warn('[FogState] persist failed:', error.message);
      });
  }

  private _expandCircle(cq: number, cr: number, radius: number): void {
    for (let dq = -radius; dq <= radius; dq++) {
      const drMin = Math.max(-radius, -dq - radius);
      const drMax = Math.min(radius, -dq + radius);
      for (let dr = drMin; dr <= drMax; dr++) {
        this._revealedKeys.add(axialKey(cq + dq, cr + dr));
      }
    }
  }
}
